import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { PHONE_DISPLAY, waDefault } from "@/lib/api";

const LINKS = [
  { label: "Inicio", to: "/" },
  { label: "Propiedades", to: "/propiedades" },
  { label: "Nosotros", to: "/#nosotros" },
  { label: "Contacto", to: "/#contacto" },
];

export const scrollToHash = (hash) => {
  const el = document.getElementById(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (to.startsWith("/#")) {
      const hash = to.slice(2);
      if (pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToHash(hash), 450);
      } else scrollToHash(hash);
    } else navigate(to);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-gold/20 py-2" : "bg-transparent py-4"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8">
          <a href="/" onClick={go("/")} data-testid="navbar-logo-link" className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Inmobiliaria Tecnohogar" className="h-11 w-auto md:h-12" />
          </a>
          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.to}
                onClick={go(l.to)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-[13px] font-medium uppercase tracking-[0.18em] text-ivory/80 transition-colors hover:text-gold"
              >
                {l.label}
              </a>
            ))}
            <a
              href={waDefault()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className="rounded-full border border-wa/40 bg-wa/10 px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-wa transition-all hover:bg-wa hover:text-ink"
            >
              WhatsApp {PHONE_DISPLAY}
            </a>
          </nav>
          <button
            className="rounded-full border border-gold/30 p-2.5 text-gold md:hidden"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-toggle"
            aria-label="Abrir menú"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink/97 backdrop-blur-xl md:hidden"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <img src="/logo.jpg" alt="Inmobiliaria Tecnohogar" className="h-11 w-auto" />
              <button
                className="rounded-full border border-gold/30 p-2.5 text-gold"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-close"
                aria-label="Cerrar menú"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-center justify-center gap-7">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.to}
                  onClick={go(l.to)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className="font-serif text-3xl uppercase tracking-wide text-ivory hover:text-gold"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href={waDefault()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp-button"
                className="mt-4 rounded-full bg-wa px-8 py-3.5 font-semibold text-ink"
              >
                WhatsApp {PHONE_DISPLAY}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
